/**
 * Top-level shot simulation. `simulate` runs a deterministic fixed-step solve to
 * rest, returning the contact event log (for the rules engine + AI) and a stream
 * of render frames. `sampleAt` interpolates those frames so the renderer can
 * animate the shot smoothly and frame-rate-independently (and scrub for replay).
 */
import type { BallId, CueParams, GameEvent, Vec3 } from '../types';
import type { BallSnapshot } from '../types';
import { applyStrike } from './strike';
import type { Frame, RenderBall } from './model';
import { quatSlerp } from './vec';
import { World } from './world';

/** Fixed physics timestep. 1 ms keeps step distance well under a ball radius. */
export const PHYS_DT = 1 / 1000;
/** Render-frame sampling interval stored for playback. */
export const FRAME_DT = 1 / 120;
/** Safety cap on shot duration (s). */
export const MAX_SHOT_TIME = 25;

export interface SimulationResult {
  events: GameEvent[];
  frames: Frame[];
  finalState: Record<BallId, BallSnapshot>;
  duration: number;
}

/**
 * Tuning for an approximate, faster sim. The AI evaluates hundreds of candidate
 * shots per move, and only needs the contact log + final rest state — not a
 * smooth render stream. A coarser physics step and sparse frames cut that cost
 * several-fold. The chosen shot is always replayed through the default fine sim
 * for the actual animation, so the player never sees the approximation.
 */
export interface SimOptions {
  /** Physics timestep (s). Larger = faster but less exact. Default PHYS_DT (1 ms). */
  dt?: number;
  /** Frame sampling interval (s). Larger = fewer frames stored. Default FRAME_DT. */
  frameDt?: number;
}

/**
 * Simulate a shot on a fresh clone of `base` (the passed world is not mutated,
 * so the AI can run many candidate shots against one base layout).
 */
export function simulate(base: World, cue: CueParams, opts?: SimOptions): SimulationResult {
  const dt = opts?.dt ?? PHYS_DT;
  const frameDt = opts?.frameDt ?? FRAME_DT;
  const world = base.clone();
  applyStrike(world.get(cue.ball), cue, world.constants);

  const events: GameEvent[] = [{ kind: 'strike', t: 0, ball: cue.ball }];
  const frames: Frame[] = [world.frame(0)];

  let t = 0;
  let nextFrame = frameDt;
  const maxSteps = Math.ceil(MAX_SHOT_TIME / dt);

  for (let step = 0; step < maxSteps; step++) {
    const evs = world.step(dt, t);
    if (evs.length) events.push(...evs);
    t += dt;
    if (t >= nextFrame) {
      frames.push(world.frame(t));
      nextFrame += frameDt;
    }
    if (world.isSettled()) break;
  }

  frames.push(world.frame(t));
  events.push({ kind: 'settled', t });
  return { events, frames, finalState: world.snapshot(), duration: t };
}

function lerpVec(a: Vec3, b: Vec3, s: number): Vec3 {
  return { x: a.x + (b.x - a.x) * s, y: a.y + (b.y - a.y) * s, z: a.z + (b.z - a.z) * s };
}

/** Interpolated ball transforms at time `t` within a simulation result. */
export function sampleAt(result: SimulationResult, t: number): Record<BallId, RenderBall> {
  const frames = result.frames;
  const out = {} as Record<BallId, RenderBall>;

  if (t <= frames[0].t) return copyFrame(frames[0]);
  const last = frames[frames.length - 1];
  if (t >= last.t) return copyFrame(last);

  // binary search for the bracketing frames
  let lo = 0;
  let hi = frames.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (frames[mid].t <= t) lo = mid;
    else hi = mid;
  }
  const f0 = frames[lo];
  const f1 = frames[hi];
  const span = f1.t - f0.t;
  const s = span > 0 ? (t - f0.t) / span : 0;

  for (const id of Object.keys(f0.balls) as BallId[]) {
    const a = f0.balls[id];
    const b = f1.balls[id];
    out[id] = { pos: lerpVec(a.pos, b.pos, s), orientation: quatSlerp(a.orientation, b.orientation, s) };
  }
  return out;
}

function copyFrame(f: Frame): Record<BallId, RenderBall> {
  const out = {} as Record<BallId, RenderBall>;
  for (const id of Object.keys(f.balls) as BallId[]) {
    out[id] = { pos: { ...f.balls[id].pos }, orientation: { ...f.balls[id].orientation } };
  }
  return out;
}
